import type { Chart, ChartType, Plugin } from 'chart.js'
import { color } from 'chart.js/helpers'

import {
  TDatasetMarker,
  TDrawMarkersPluginOptions
} from 'components_new/molecules/charts/chartjs-plugins'

import {
  TMarkerGroup,
  drawPin,
  findHitPinSquare,
  getChartMarkers,
  getMarkerGroups,
  getMarkerLayoutPadding,
  getMarkerRadius,
  getPinPointerHeight,
  getPinPosition,
  getStackDepth
} from './drawPin'
import { setCursor } from '../utils/cursor'

const PLUGIN_ID = 'drawMarkers'

const num = (v: unknown, fb: number) => (typeof v === 'number' ? v : fb)
const str = (v: unknown, fb: string) => (typeof v === 'string' ? v : fb)

const STEM_DOT_RADIUS = 2.5
// share of the pin's own colour when hovered; the rest is white
const HOVER_FILL_WEIGHT = 0.7

const hoveredByChart = new WeakMap<Chart, TMarkerGroup | null>()

// markers of the hovered pin, listed by the chart tooltip instead of the data
export const getHoveredMarkers = (chart: Chart): TDatasetMarker[] =>
  hoveredByChart.get(chart)?.markers ?? []

export const drawMarkers: Plugin<ChartType, TDrawMarkersPluginOptions> = {
  id: 'drawMarkers',

  // room for the pins, so none is cut off at the top or either end
  beforeLayout(chart, _args, opt) {
    const markers = getChartMarkers(chart)
    const padding = markers.length
      ? getMarkerLayoutPadding(
        Math.max(...markers.map((m) => getMarkerRadius(m, opt))),
        num(opt.stemHeight, 0)
      )
      : { left: 0, right: 0, top: 0 }
    const layout = chart.options.layout as any
    const current = typeof layout.padding === 'object' ? layout.padding : {}

    // right is shared with drawLatestValueLabel, which sets it first
    layout.padding = {
      ...current,
      left: padding.left,
      right: Math.max(current.right ?? 0, padding.right),
      top: padding.top
    }
  },

  afterDatasetsDraw(chart, _args, opt) {
    const { ctx } = chart
    const hoveredKey = hoveredByChart.get(chart)?.key ?? null

    const bw0 = num(opt.borderWidth, 1)
    const bg0 = str(opt.backgroundColor, '#000')
    const bc0 = str(opt.borderColor, '#fff')

    const font0 = str(opt.charFont, '10px sans-serif')
    const cc0 = str(opt.charColor, '#fff')

    getMarkerGroups(chart, opt, hoveredKey).forEach((group) => {
      const first = group.markers[0]
      const stemColor = str(first.backgroundColor, bg0)

      ctx.save()
      ctx.lineWidth = 1
      ctx.strokeStyle = stemColor

      // stem from the line up to a raised pin
      if (group.tipY < group.yLine) {
        ctx.beginPath()
        ctx.moveTo(group.x, group.yLine)
        ctx.lineTo(group.x, group.tipY)
        ctx.stroke()

        ctx.beginPath()
        ctx.arc(group.x, group.yLine, STEM_DOT_RADIUS, 0, Math.PI * 2)
        ctx.fillStyle = stemColor
        ctx.fill()
        ctx.strokeStyle = str(first.borderColor, bc0)
        ctx.stroke()
      }

      ctx.restore()

      // back to front, so the first marker's pin is drawn on top
      for (let layer = getStackDepth(group); layer >= 0; layer--) {
        const m = group.markers[layer]
        const r = getMarkerRadius(m, opt)
        const bw = num(m.borderWidth, bw0)
        const { tipY, x } = getPinPosition(group, layer)

        ctx.save()

        // pin
        const size = r * 2
        const cornerR = Math.max(2, r * 0.4)
        const pointerH = getPinPointerHeight(r)
        const pointerW = Math.max(6, r * 1.1)

        const fill = group.hovered
          ? color(str(m.backgroundColor, bg0))
            .mix(color('#ffffff'), HOVER_FILL_WEIGHT)
            .rgbString()
          : str(m.backgroundColor, bg0)
        const stroke = str(m.borderColor, bc0)

        const { cx, cy } = drawPin(
          ctx,
          x,
          tipY,
          size,
          cornerR,
          pointerH,
          pointerW,
          fill,
          stroke,
          bw
        )

        const ch = (m.char ?? '').slice(0, 1)

        if (ch && layer === 0) {
          ctx.font = str(m.charFont, font0)
          ctx.fillStyle = str(m.charColor, cc0)
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          ctx.fillText(ch, cx, cy)
        }

        ctx.restore()
      }
    })
  },

  beforeEvent(chart, args, opt) {
    const ev = args.event
    const ex = ev.x
    const ey = ev.y
    const prev = hoveredByChart.get(chart)?.key ?? null

    // Hover tracking
    if (ev.type === 'mousemove' || ev.type === 'mouseout') {
      const hit =
        ev.type === 'mousemove' && ex != null && ey != null
          ? findHitPinSquare(chart, ex, ey, opt, prev)
          : null

      setCursor(chart, PLUGIN_ID, hit ? 'pointer' : null)

      if (prev !== (hit?.key ?? null)) {
        hoveredByChart.set(chart, hit)
        chart.draw() // redraw so the hovered pins are lighter

        // the chart tooltip lists the pin's markers, beside the pin
        if (hit) {
          chart.tooltip?.setActiveElements(
            [{ datasetIndex: hit.datasetIndex, index: hit.markers[0].index }],
            {
              x: hit.x,
              y:
                getPinPosition(hit, getStackDepth(hit)).tipY -
                getPinPointerHeight(hit.r) -
                hit.r
            }
          )
        }
      }

      // keep Chart.js from moving the tooltip back to the line
      if (hit) return false
    }

    // Click handling (optional)
    if (!opt.onClick || ev.type !== 'click') return
    if (ex == null || ey == null) return

    const hit = findHitPinSquare(chart, ex, ey, opt, prev)

    if (hit) {
      opt.onClick({ event: ev, markers: hit.markers, chart })

      // stop Chart.js + DOM handling
      args.event.native?.stopPropagation()
      args.event.native?.preventDefault()

      return false
    }

    return
  }
}
